import React, { useState, useEffect } from 'react';
import ExpireRenewDialog from './ExpireRenewDialog';
import DeleteLicenseDialog from './DeleteLicenseDialog';

interface License {
  id: string;
  product: string;
  clientName: string | null;
  validUntil: string;
  createdAt: string;
  updatedAt: string;
}

interface LicenseTableProps {
  apiUrl: string;
  authToken: string;
  onRefresh: () => void;
  refreshKey?: number;
}

const LicenseTable: React.FC<LicenseTableProps> = ({ apiUrl, authToken, onRefresh, refreshKey = 0 }) => {
  const [licenses, setLicenses] = useState<License[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expireRenewDialog, setExpireRenewDialog] = useState<{
    license: License;
    action: 'expire' | 'renew';
  } | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<License | null>(null);

  const fetchLicenses = React.useCallback(async () => {
    setLoading(true);
    try {
      const url = new URL(`${apiUrl}/api/licenses`);
      if (search) {
        url.searchParams.append('search', search);
      }
      
      const response = await fetch(url.toString(), {
        headers: {
          'Authorization': `Basic ${authToken}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setLicenses(data.licenses);
      }
    } catch (error) {
      console.error('Failed to fetch licenses:', error);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, authToken, search]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchLicenses();
    }, 300); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [fetchLicenses]);

  // Initial load and refresh when refreshKey changes (triggered by parent after add/update/delete)
  useEffect(() => {
    fetchLicenses();
  }, [refreshKey, fetchLicenses]);


  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Licenses</h2>
        </div>
        <input
          type="text"
          placeholder="Search by identifier, product, or client name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {loading ? (
        <div className="p-8 text-center text-gray-500">Loading...</div>
      ) : licenses.length === 0 ? (
        <div className="p-8 text-center text-gray-500">No licenses found</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Identifier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valid Until
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {licenses.map((license) => (
                <tr key={`${license.id}:${license.product}`}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {license.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {license.product}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {license.clientName || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>
                      {new Date(license.validUntil).toLocaleDateString()}
                      {new Date(license.validUntil) < new Date() && (
                        <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-800 rounded">
                          Expired
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(license.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => setExpireRenewDialog({ license, action: 'expire' })}
                        className="text-orange-600 hover:text-orange-900"
                      >
                        Expire
                      </button>
                      <button
                        onClick={() => setExpireRenewDialog({ license, action: 'renew' })}
                        className="text-green-600 hover:text-green-900"
                      >
                        Renew
                      </button>
                      <button
                        onClick={() => setDeleteDialog(license)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {expireRenewDialog && (
        <ExpireRenewDialog
          apiUrl={apiUrl}
          authToken={authToken}
          license={expireRenewDialog.license}
          action={expireRenewDialog.action}
          onClose={() => setExpireRenewDialog(null)}
          onSuccess={() => {
            setExpireRenewDialog(null);
            fetchLicenses();
            onRefresh();
          }}
        />
      )}

      {deleteDialog && (
        <DeleteLicenseDialog
          apiUrl={apiUrl}
          authToken={authToken}
          license={deleteDialog}
          onClose={() => setDeleteDialog(null)}
          onSuccess={() => {
            setDeleteDialog(null);
            fetchLicenses();
            onRefresh();
          }}
        />
      )}
    </div>
  );
};

export default LicenseTable;

